import React, { useState } from "react";
import { useParams } from "react-router-dom";
import Layout from "../components/layouts/Layout";
import { sendContactNotificationToSlack, testSlackConnection } from "../utils/slackNotification";
import { sendAutoReplyEmail } from "../utils/emailService";
import { toast } from "react-toastify";

const ContactPage = () => {
  const { type } = useParams();
  const [activeTab, setActiveTab] = useState(type || 'general');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
    interest: '',
    budget: '',
    timeline: ''
  });

  // テスト関数を追加
  const handleTestSlack = async () => {
    console.log('🧪 Testing Slack connection...');
    const result = await testSlackConnection();
    if (result) {
      toast.success('✅ Slack接続テスト成功！');
    } else {
      toast.error('❌ Slack接続テスト失敗');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // 1. EmailJS送信（失敗してもSlack送信は続行）
      try {
        await sendAutoReplyEmail(formData, activeTab);
        console.log('✅ Auto-reply email sent');
      } catch (emailError) {
        console.warn('⚠️ Auto-reply email failed:', emailError);
      }

      // 2. Slack通知送信
      const slackSuccess = await sendContactNotificationToSlack(formData, activeTab);
      
      if (slackSuccess) {
        toast.success('お問い合わせを受付いたしました。担当者よりご連絡させていただきます。');
      } else {
        // Slack失敗でもフォーム送信は成功として扱う
        toast.success('お問い合わせを受付いたしました。担当者よりご連絡させていただきます。');
        console.warn('Slack notification failed, but form submission recorded');
      }
      
      // フォームリセット
      setFormData({ 
        name: '', email: '', company: '', phone: '', message: '',
        interest: '', budget: '', timeline: '' 
      });

    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('送信に失敗しました。しばらく後でもう一度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = {
    address: "〒107-0061　東京都港区北青山一丁目3番1号　アールキューブ青山3階",
    phone: "090-9147-7782",
    email: "info@deeptell.jp",
    hours: "平日 9:00-18:00",
    map: "https://maps.app.goo.gl/GLoaLD2ThhWHS4jZA"
  };

  return (
    <Layout 
      title="お問い合わせ" 
      subtitle="DeepTeLLへのご質問、ご相談、資料請求はこちらから"
    >
      {/* 開発環境でのみテストボタンを表示 */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{ 
          position: 'fixed', 
          top: '10px', 
          right: '10px', 
          zIndex: 1000,
          background: '#1e293b',
          padding: '10px',
          borderRadius: '8px',
          border: '1px solid #3b82f6'
        }}>
          <button 
            onClick={handleTestSlack}
            style={{
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            🧪 Slack テスト
          </button>
        </div>
      )}

      {/* タブナビゲーション */}
      <section style={{ padding: '60px 0 40px', backgroundColor: '#0f172a' }}>
        <div className="container">
          <div className="row">
            <div className="col-md-12">
              <div className="text-center">
                <div style={{ display: 'inline-flex', background: 'rgba(30, 41, 59, 0.8)', borderRadius: '8px', padding: '4px' }}>
                  <button
                    className={`btn ${activeTab === 'general' ? 'btn-custom' : 'btn-outline'}`}
                    onClick={() => setActiveTab('general')}
                    style={{ 
                      margin: '4px',
                      background: activeTab === 'general' ? '#3b82f6' : 'transparent',
                      color: activeTab === 'general' ? 'white' : '#cbd5e1'
                    }}
                  >
                    一般問い合わせ
                  </button>
                  <button
                    className={`btn ${activeTab === 'materials' ? 'btn-custom' : 'btn-outline'}`}
                    onClick={() => setActiveTab('materials')}
                    style={{ 
                      margin: '4px',
                      background: activeTab === 'materials' ? '#3b82f6' : 'transparent',
                      color: activeTab === 'materials' ? 'white' : '#cbd5e1'
                    }}
                  >
                    資料請求
                  </button>
                  <button
                    className={`btn ${activeTab === 'demo' ? 'btn-custom' : 'btn-outline'}`}
                    onClick={() => setActiveTab('demo')}
                    style={{ 
                      margin: '4px',
                      background: activeTab === 'demo' ? '#3b82f6' : 'transparent',
                      color: activeTab === 'demo' ? 'white' : '#cbd5e1'
                    }}
                  >
                    デモ申し込み
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* コンタクトフォーム */}
      <section style={{ padding: '80px 0', backgroundColor: '#1e293b' }}>
        <div className="container">
          <div className="row">
            <div className="col-md-8">
              <div className="premium-card" style={{ 
                padding: '40px',
                background: 'rgba(30, 41, 59, 0.8)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                borderRadius: '12px'
              }}>
                {/* 一般問い合わせ */}
                {activeTab === 'general' && (
                  <>
                    <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                      <h2 style={{ color: '#3b82f6', marginBottom: '15px' }}>一般問い合わせ</h2>
                      <p style={{ color: '#cbd5e1' }}>
                        サービスに関するご質問やご相談など、お気軽にお問い合わせください
                      </p>
                    </div>

                    <form onSubmit={handleSubmit}>
                      <div className="row">
                        <div className="col-md-6">
                          <div style={{ marginBottom: '20px' }}>
                            <label style={{ color: '#f1f5f9', marginBottom: '8px', display: 'block' }}>
                              お名前 *
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              value={formData.name}
                              onChange={(e) => setFormData({...formData, name: e.target.value})}
                              required
                              style={{ 
                                background: 'rgba(30, 41, 59, 0.8)',
                                border: '1px solid #475569',
                                color: '#e2e8f0'
                              }}
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div style={{ marginBottom: '20px' }}>
                            <label style={{ color: '#f1f5f9', marginBottom: '8px', display: 'block' }}>
                              メールアドレス *
                            </label>
                            <input
                              type="email"
                              className="form-control"
                              value={formData.email}
                              onChange={(e) => setFormData({...formData, email: e.target.value})}
                              required
                              style={{ 
                                background: 'rgba(30, 41, 59, 0.8)',
                                border: '1px solid #475569',
                                color: '#e2e8f0'
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <div style={{ marginBottom: '20px' }}>
                            <label style={{ color: '#f1f5f9', marginBottom: '8px', display: 'block' }}>
                              会社名
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              value={formData.company}
                              onChange={(e) => setFormData({...formData, company: e.target.value})}
                              style={{ 
                                background: 'rgba(30, 41, 59, 0.8)',
                                border: '1px solid #475569',
                                color: '#e2e8f0'
                              }}
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div style={{ marginBottom: '20px' }}>
                            <label style={{ color: '#f1f5f9', marginBottom: '8px', display: 'block' }}>
                              電話番号
                            </label>
                            <input
                              type="tel"
                              className="form-control"
                              value={formData.phone}
                              onChange={(e) => setFormData({...formData, phone: e.target.value})}
                              style={{ 
                                background: 'rgba(30, 41, 59, 0.8)',
                                border: '1px solid #475569',
                                color: '#e2e8f0'
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      <div style={{ marginBottom: '30px' }}>
                        <label style={{ color: '#f1f5f9', marginBottom: '8px', display: 'block' }}>
                          お問い合わせ内容 *
                        </label>
                        <textarea
                          className="form-control"
                          rows="5"
                          value={formData.message}
                          onChange={(e) => setFormData({...formData, message: e.target.value})}
                          required
                          placeholder="具体的なご質問内容やご相談事項をお聞かせください"
                          style={{ 
                            background: 'rgba(30, 41, 59, 0.8)',
                            border: '1px solid #475569',
                            color: '#e2e8f0'
                          }}
                        />
                      </div>

                      <button 
                        type="submit" 
                        className="btn btn-custom btn-lg" 
                        style={{ width: '100%' }}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? '送信中...' : '送信する'}
                      </button>
                    </form>
                  </>
                )}

                {/* 資料請求 */}
                {activeTab === 'materials' && (
                  <>
                    <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                      <h2 style={{ color: '#3b82f6', marginBottom: '15px' }}>資料請求</h2>
                      <p style={{ color: '#cbd5e1' }}>
                        DeepTeLLのサービス資料、技術資料をご請求いただけます
                      </p>
                    </div>

                    <form onSubmit={handleSubmit}>
                      <div className="row">
                        <div className="col-md-6">
                          <div style={{ marginBottom: '20px' }}>
                            <label style={{ color: '#f1f5f9', marginBottom: '8px', display: 'block' }}>
                              お名前 *
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              value={formData.name}
                              onChange={(e) => setFormData({...formData, name: e.target.value})}
                              required
                              style={{ 
                                background: 'rgba(30, 41, 59, 0.8)',
                                border: '1px solid #475569',
                                color: '#e2e8f0'
                              }}
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div style={{ marginBottom: '20px' }}>
                            <label style={{ color: '#f1f5f9', marginBottom: '8px', display: 'block' }}>
                              メールアドレス *
                            </label>
                            <input
                              type="email"
                              className="form-control"
                              value={formData.email}
                              onChange={(e) => setFormData({...formData, email: e.target.value})}
                              required
                              style={{ 
                                background: 'rgba(30, 41, 59, 0.8)',
                                border: '1px solid #475569',
                                color: '#e2e8f0'
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      <div style={{ marginBottom: '20px' }}>
                        <label style={{ color: '#f1f5f9', marginBottom: '8px', display: 'block' }}>
                          会社名 *
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.company}
                          onChange={(e) => setFormData({...formData, company: e.target.value})}
                          required
                          style={{ 
                            background: 'rgba(30, 41, 59, 0.8)',
                            border: '1px solid #475569',
                            color: '#e2e8f0'
                          }}
                        />
                      </div>

                      <div style={{ marginBottom: '20px' }}>
                        <label style={{ color: '#f1f5f9', marginBottom: '8px', display: 'block' }}>
                          ご関心のあるソリューション *
                        </label>
                        <select
                          className="form-control"
                          value={formData.interest}
                          onChange={(e) => setFormData({...formData, interest: e.target.value})}
                          required
                          style={{ 
                            background: 'rgba(30, 41, 59, 0.8)',
                            border: '1px solid #475569',
                            color: '#e2e8f0'
                          }}
                        >
                          <option value="">選択してください</option>
                          <option value="quantum-ai">量子AIソリューション</option>
                          <option value="finance">金融業界向けAI</option>
                          <option value="manufacturing">製造業向けAI</option>
                          <option value="healthcare">ヘルスケアAI</option>
                          <option value="predictive">予知保全システム</option>
                          <option value="platform">AIプラットフォーム全般</option>
                        </select>
                      </div>

                      <div style={{ marginBottom: '30px' }}>
                        <label style={{ color: '#f1f5f9', marginBottom: '8px', display: 'block' }}>
                          その他ご要望
                        </label>
                        <textarea
                          className="form-control"
                          rows="3"
                          value={formData.message}
                          onChange={(e) => setFormData({...formData, message: e.target.value})}
                          placeholder="特定の資料や詳細情報をご希望の場合はこちらにご記入ください"
                          style={{ 
                            background: 'rgba(30, 41, 59, 0.8)',
                            border: '1px solid #475569',
                            color: '#e2e8f0'
                          }}
                        />
                      </div>

                      <button 
                        type="submit" 
                        className="btn btn-custom btn-lg" 
                        style={{ width: '100%' }}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? '送信中...' : '資料を請求する'}
                      </button>
                    </form>
                  </>
                )}

                {/* デモ申し込み */}
                {activeTab === 'demo' && (
                  <>
                    <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                      <h2 style={{ color: '#3b82f6', marginBottom: '15px' }}>デモ申し込み</h2>
                      <p style={{ color: '#cbd5e1' }}>
                        実際のシステムをご体験いただき、導入効果をご確認ください
                      </p>
                    </div>

                    <form onSubmit={handleSubmit}>
                      <div className="row">
                        <div className="col-md-6">
                          <div style={{ marginBottom: '20px' }}>
                            <label style={{ color: '#f1f5f9', marginBottom: '8px', display: 'block' }}>
                              お名前 *
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              value={formData.name}
                              onChange={(e) => setFormData({...formData, name: e.target.value})}
                              required
                              style={{ 
                                background: 'rgba(30, 41, 59, 0.8)',
                                border: '1px solid #475569',
                                color: '#e2e8f0'
                              }}
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div style={{ marginBottom: '20px' }}>
                            <label style={{ color: '#f1f5f9', marginBottom: '8px', display: 'block' }}>
                              メールアドレス *
                            </label>
                            <input
                              type="email"
                              className="form-control"
                              value={formData.email}
                              onChange={(e) => setFormData({...formData, email: e.target.value})}
                              required
                              style={{ 
                                background: 'rgba(30, 41, 59, 0.8)',
                                border: '1px solid #475569',
                                color: '#e2e8f0'
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <div style={{ marginBottom: '20px' }}>
                            <label style={{ color: '#f1f5f9', marginBottom: '8px', display: 'block' }}>
                              会社名 *
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              value={formData.company}
                              onChange={(e) => setFormData({...formData, company: e.target.value})}
                              required
                              style={{ 
                                background: 'rgba(30, 41, 59, 0.8)',
                                border: '1px solid #475569',
                                color: '#e2e8f0'
                              }}
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div style={{ marginBottom: '20px' }}>
                            <label style={{ color: '#f1f5f9', marginBottom: '8px', display: 'block' }}>
                              電話番号 *
                            </label>
                            <input
                              type="tel"
                              className="form-control"
                              value={formData.phone}
                              onChange={(e) => setFormData({...formData, phone: e.target.value})}
                              required
                              style={{ 
                                background: 'rgba(30, 41, 59, 0.8)',
                                border: '1px solid #475569',
                                color: '#e2e8f0'
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <div style={{ marginBottom: '20px' }}>
                            <label style={{ color: '#f1f5f9', marginBottom: '8px', display: 'block' }}>
                              導入予算
                            </label>
                            <select
                              className="form-control"
                              value={formData.budget}
                              onChange={(e) => setFormData({...formData, budget: e.target.value})}
                              style={{ 
                                background: 'rgba(30, 41, 59, 0.8)',
                                border: '1px solid #475569',
                                color: '#e2e8f0'
                              }}
                            >
                              <option value="">選択してください</option>
                              <option value="under-1m">100万円未満</option>
                              <option value="1m-5m">100万円〜500万円</option>
                              <option value="5m-10m">500万円〜1,000万円</option>
                              <option value="over-10m">1,000万円以上</option>
                              <option value="undecided">未定</option>
                            </select>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div style={{ marginBottom: '20px' }}>
                            <label style={{ color: '#f1f5f9', marginBottom: '8px', display: 'block' }}>
                              導入時期
                            </label>
                            <select
                              className="form-control"
                              value={formData.timeline}
                              onChange={(e) => setFormData({...formData, timeline: e.target.value})}
                              style={{ 
                                background: 'rgba(30, 41, 59, 0.8)',
                                border: '1px solid #475569',
                                color: '#e2e8f0'
                              }}
                            >
                              <option value="">選択してください</option>
                              <option value="immediately">すぐに</option>
                              <option value="3months">3ヶ月以内</option>
                              <option value="6months">6ヶ月以内</option>
                              <option value="1year">1年以内</option>
                              <option value="undecided">未定</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <div style={{ marginBottom: '30px' }}>
                        <label style={{ color: '#f1f5f9', marginBottom: '8px', display: 'block' }}>
                          デモでご確認したい内容
                        </label>
                        <textarea
                          className="form-control"
                          rows="4"
                          value={formData.message}
                          onChange={(e) => setFormData({...formData, message: e.target.value})}
                          placeholder="具体的な課題や確認したい機能があればお聞かせください"
                          style={{ 
                            background: 'rgba(30, 41, 59, 0.8)',
                            border: '1px solid #475569',
                            color: '#e2e8f0'
                          }}
                        />
                      </div>

                      <button 
                        type="submit" 
                        className="btn btn-custom btn-lg" 
                        style={{ width: '100%' }}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? '送信中...' : 'デモを申し込む'}
                      </button>
                    </form>
                  </>
                )}
              </div>
            </div>

            {/* 企業情報サイドバー */}
            <div className="col-md-4">
              <div className="premium-card" style={{ 
                padding: '30px',
                marginBottom: '20px',
                background: 'rgba(30, 41, 59, 0.8)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                borderRadius: '12px'
              }}>
                <h4 style={{ color: '#f1f5f9', marginBottom: '20px' }}>企業情報</h4>
                
                <div style={{ marginBottom: '20px' }}>
                  <h6 style={{ color: '#3b82f6', marginBottom: '8px' }}>所在地</h6>
                  <p style={{ color: '#cbd5e1', fontSize: '14px' }}>{contactInfo.address}</p>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <h6 style={{ color: '#3b82f6', marginBottom: '8px' }}>電話番号</h6>
                  <p style={{ color: '#cbd5e1', fontSize: '14px' }}>{contactInfo.phone}</p>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <h6 style={{ color: '#3b82f6', marginBottom: '8px' }}>メールアドレス</h6>
                  <p style={{ color: '#cbd5e1', fontSize: '14px' }}>{contactInfo.email}</p>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <h6 style={{ color: '#3b82f6', marginBottom: '8px' }}>営業時間</h6>
                  <p style={{ color: '#cbd5e1', fontSize: '14px' }}>{contactInfo.hours}</p>
                </div>
              </div>

              {/* レスポンス時間 */}
              <div className="premium-card" style={{ 
                padding: '25px',
                background: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.2)',
                borderRadius: '12px'
              }}>
                <h5 style={{ color: '#10b981', marginBottom: '15px' }}>サポート対応</h5>
                <ul style={{ color: '#cbd5e1', fontSize: '14px', marginLeft: '0', listStyle: 'none' }}>
                  <li style={{ marginBottom: '8px' }}>✅ 一般問い合わせ：2営業日以内</li>
                  <li style={{ marginBottom: '8px' }}>✅ 資料請求：即日対応</li>
                  <li style={{ marginBottom: '8px' }}>✅ デモ申し込み：1営業日以内</li>
                  <li>✅ 緊急時：24時間以内</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* アクセス情報セクション */}
      <section style={{ padding: '80px 0', backgroundColor: '#0f172a' }}>
        <div className="container">
          <div className="row">
            <div className="col-md-12">
              <div style={{ textAlign: 'center', marginBottom: '50px' }}>
                <h2 style={{ color: '#3b82f6', marginBottom: '15px' }}>アクセス</h2>
                <p style={{ color: '#cbd5e1' }}>
                  お気軽にお越しください。最寄り駅からのアクセスも良好です。
                </p>
              </div>
            </div>
          </div>
          
          <div className="row">
            {/* 地図 */}
            <div className="col-md-8">
              <div style={{ 
                background: 'rgba(30, 41, 59, 0.8)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                borderRadius: '12px',
                padding: '20px',
                height: '500px'
              }}>
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3241.1736605250208!2d139.72065767597908!3d35.672725730440824!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x60188dfac9ad3511%3A0xdb4ae2c1f35a9746!2z5p2x5Lqs6YO95riv5Yy65YyX6Z2S5bGxMS0zLTE!5e0!3m2!1sja!2sjp!4v1749181508681!5m2!1sja!2sjp" 
                  width="100%" 
                  height="460" 
                  style={{ 
                    border: 0,
                    borderRadius: '8px'
                  }} 
                  allowFullScreen="" 
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade"
                  title="DeepTeLLオフィス所在地"
                />
              </div>
            </div>
            
            {/* アクセス情報 */}
            <div className="col-md-4">
              <div style={{ 
                background: 'rgba(30, 41, 59, 0.8)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                borderRadius: '12px',
                padding: '30px',
                height: '500px'
              }}>
                <h3 style={{ color: '#f1f5f9', marginBottom: '30px' }}>オフィス情報</h3>
                
                <div style={{ marginBottom: '25px' }}>
                  <h4 style={{ color: '#3b82f6', marginBottom: '10px', fontSize: '16px' }}>住所</h4>
                  <p style={{ color: '#cbd5e1', margin: 0, lineHeight: '1.6' }}>
                    〒107-0061<br />
                    東京都港区北青山1-3-1
                  </p>
                </div>
                
                <div style={{ marginBottom: '25px' }}>
                  <h4 style={{ color: '#3b82f6', marginBottom: '10px', fontSize: '16px' }}>最寄り駅</h4>
                  <ul style={{ color: '#cbd5e1', margin: 0, paddingLeft: '20px' }}>
                    <li style={{ marginBottom: '5px' }}>地下鉄銀座線・半蔵門線・大江戸線「青山一丁目駅」徒歩3分</li>
                    <li style={{ marginBottom: '5px' }}>地下鉄銀座線・丸ノ内線「赤坂見附駅」徒歩8分</li>
                  </ul>
                </div>
                
                <div style={{ marginBottom: '25px' }}>
                  <h4 style={{ color: '#3b82f6', marginBottom: '10px', fontSize: '16px' }}>営業時間</h4>
                  <p style={{ color: '#cbd5e1', margin: 0 }}>
                    平日 9:00 - 18:00<br />
                    ※土日祝日は休業
                  </p>
                </div>
                
                <div>
                  <h4 style={{ color: '#3b82f6', marginBottom: '10px', fontSize: '16px' }}>お問い合わせ</h4>
                  <p style={{ color: '#cbd5e1', margin: 0 }}>
                    Email: info@deeptell.com<br />
                    Tel: 03-XXXX-XXXX
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ContactPage;

<style jsx>{`
  @media (max-width: 768px) {
    .col-md-8, .col-md-4 {
      margin-bottom: 20px;
    }
    
    iframe {
      height: 300px !important;
    }
    
    .row > div:last-child > div {
      height: auto !important;
    }
  }
`}</style> 